import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import useSWR from "swr";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Folder, FileText, ChevronRight, ArrowUp, RefreshCcw, FilePlus, FolderPlus,
  Save, Trash2, HardDrive, Home
} from "lucide-react";
import { toast } from "sonner";

const fetcher = (u) => api.get(u).then((r) => r.data);

function joinPath(parts) {
  const p = parts.filter(Boolean).join("/").replace(/\/+/g, "/");
  return p.startsWith("/") ? p : "/" + p;
}

export function SiteFiles({ site }) {
  const { data: info } = useSWR(`/sites/${site.id}/containers`, fetcher);
  const [container, setContainer] = useState("");
  const [cwd, setCwd] = useState("");        // relative path from source root
  const [selectedFile, setSelectedFile] = useState(null);
  const [content, setContent] = useState("");
  const [origContent, setOrigContent] = useState("");
  const [refreshTick, setRefreshTick] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (info && !container) setContainer(info.default);
  }, [info, container]);

  const mode = info?.mode || "local";
  const listUrl = `/sites/${site.id}/fs?path=${encodeURIComponent(cwd)}` +
                  (container ? `&container=${encodeURIComponent(container)}` : "") +
                  `&_=${refreshTick}`;
  const { data: listing, mutate: refetchList } = useSWR(
    () => listUrl, fetcher,
    { revalidateOnFocus: false }
  );

  const openFile = async (name) => {
    const p = joinPath([cwd, name]);
    try {
      const { data } = await api.get(
        `/sites/${site.id}/fs/read?path=${encodeURIComponent(p)}` +
        (container ? `&container=${encodeURIComponent(container)}` : "")
      );
      setSelectedFile(p);
      setContent(data.content || "");
      setOrigContent(data.content || "");
      if (data.encoding === "binary") toast.warning("Binary file — saving will corrupt it");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "cannot open file");
    }
  };

  const enterDir = (name) => {
    setSelectedFile(null);
    setCwd(joinPath([cwd, name]).replace(/^\//, ""));
  };

  const upDir = () => {
    setSelectedFile(null);
    const parts = cwd.split("/").filter(Boolean);
    parts.pop();
    setCwd(parts.join("/"));
  };

  const homeDir = () => { setSelectedFile(null); setCwd(""); };

  const saveFile = async () => {
    if (!selectedFile) return;
    setBusy(true);
    try {
      await api.post(`/sites/${site.id}/fs/write`, {
        path: selectedFile, content, container: container || undefined,
      });
      setOrigContent(content);
      toast.success("Saved");
      refetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "save failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteEntry = async (name, type) => {
    const p = joinPath([cwd, name]);
    try {
      await api.delete(`/sites/${site.id}/fs?path=${encodeURIComponent(p)}` +
                       (container ? `&container=${encodeURIComponent(container)}` : ""));
      toast.success(`Deleted ${type}`);
      if (selectedFile === p) { setSelectedFile(null); setContent(""); setOrigContent(""); }
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "delete failed");
    }
  };

  const createFile = async () => {
    const name = prompt("New file name (can include /)");
    if (!name) return;
    const p = joinPath([cwd, name]);
    try {
      await api.post(`/sites/${site.id}/fs/write`, {
        path: p, content: "", container: container || undefined,
      });
      toast.success("File created");
      setRefreshTick((t) => t + 1);
      openFile(name);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "create failed");
    }
  };

  const createDir = async () => {
    const name = prompt("New folder name");
    if (!name) return;
    const p = joinPath([cwd, name]);
    try {
      await api.post(`/sites/${site.id}/fs/mkdir`, {
        path: p, container: container || undefined,
      });
      toast.success("Folder created");
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "mkdir failed");
    }
  };

  const dirty = selectedFile && content !== origContent;
  const crumbs = useMemo(() => cwd.split("/").filter(Boolean), [cwd]);

  return (
    <Card data-testid="site-files-panel" className="p-0 border-border overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border surface-1 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <HardDrive className="h-4 w-4" /> Files
          <span className="text-[11px] px-1.5 py-0.5 rounded surface-2 text-muted-foreground">
            {mode === "remote" ? "container" : "source (local)"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {mode === "remote" && (
            <select
              value={container}
              onChange={(e) => { setContainer(e.target.value); setCwd(""); setSelectedFile(null); }}
              data-testid="files-container-select"
              className="h-8 text-xs rounded-md border border-input bg-transparent px-2"
            >
              {(info?.containers || []).map((c) => (
                <option key={c.name} value={c.name}>{c.label}</option>
              ))}
            </select>
          )}
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setRefreshTick((t) => t + 1)}>
            <RefreshCcw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={createFile} data-testid="files-new-file">
            <FilePlus className="h-3.5 w-3.5" /> New file
          </Button>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={createDir} data-testid="files-new-folder">
            <FolderPlus className="h-3.5 w-3.5" /> New folder
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Directory listing */}
        <div className="lg:col-span-5 border-r border-border">
          <div className="flex items-center gap-1 px-3 py-2 border-b border-border font-mono text-[12px] text-muted-foreground overflow-auto scroll-thin">
            <Button variant="ghost" size="icon" onClick={homeDir} className="h-6 w-6">
              <Home className="h-3.5 w-3.5" />
            </Button>
            <span>/</span>
            {crumbs.map((c, i) => (
              <span key={i} className="flex items-center gap-1">
                <span className="text-foreground">{c}</span>
                <ChevronRight className="h-3 w-3" />
              </span>
            ))}
            <div className="ml-auto">
              {cwd && (
                <Button variant="ghost" size="sm" className="h-6 gap-1 text-[11px]" onClick={upDir}>
                  <ArrowUp className="h-3 w-3" /> Up
                </Button>
              )}
            </div>
          </div>
          <ul className="divide-y divide-border max-h-[500px] overflow-auto scroll-thin" data-testid="files-list">
            {(listing?.entries || []).map((e) => (
              <li key={e.name}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-[hsl(var(--surface-2))]/40 cursor-pointer"
                  onClick={() => (e.type === "dir" ? enterDir(e.name) : openFile(e.name))}>
                {e.type === "dir"
                  ? <Folder className="h-4 w-4 text-primary shrink-0" />
                  : <FileText className="h-4 w-4 text-muted-foreground shrink-0" />}
                <div className="flex-1 min-w-0 truncate">{e.name}</div>
                <div className="text-[11px] text-muted-foreground font-mono shrink-0">
                  {e.type === "file" ? `${e.size}B` : ""}
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6"
                            onClick={(ev) => ev.stopPropagation()}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent onClick={(ev) => ev.stopPropagation()}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {e.name}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently removes the {e.type}. Cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteEntry(e.name, e.type)}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </li>
            ))}
            {listing && listing.entries.length === 0 && (
              <li className="px-3 py-6 text-sm text-muted-foreground text-center">
                (empty)
              </li>
            )}
          </ul>
        </div>

        {/* Editor */}
        <div className="lg:col-span-7 flex flex-col">
          {selectedFile ? (
            <>
              <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-border">
                <div className="font-mono text-[12.5px] truncate">{selectedFile}{dirty && " •"}</div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={saveFile} disabled={!dirty || busy}
                          data-testid="files-save-button" className="gap-1.5">
                    <Save className="h-3.5 w-3.5" /> Save
                  </Button>
                </div>
              </div>
              <textarea
                data-testid="files-editor-textarea"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                spellCheck={false}
                className="scroll-thin flex-1 min-h-[380px] w-full resize-none p-4 font-mono text-[12.5px] leading-6 code-surface rounded-none border-0 outline-none"
                style={{ tabSize: 2 }}
              />
            </>
          ) : (
            <div className="flex-1 grid place-items-center text-sm text-muted-foreground p-10 min-h-[300px]">
              Select a file on the left to view or edit its contents.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
